import React, { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom';
import { Button } from '@mui/material';

import { IWasteCharacterization } from '../../types/wasteCharacterization';

import { UtilityService } from '../../services/utilityService';

import { Icons } from '../icons';
import Spinner from '../spinner';

import "../../styles/entity/wasteCharacterizationList.scss";
import exp from 'constants';

export interface IWasteCharacterizationTableProps {
	data?: IWasteCharacterization[];
	profile?: string;
	material?: string;
	customer?: string;
	status?: string;
	expireWarningDays?: number;
}

const WasteCharacterizationTable: React.FunctionComponent<IWasteCharacterizationTableProps> = ({ data, profile, material, customer, status, expireWarningDays }) => {
	const [dataView, setDataView] = useState<IWasteCharacterization[]>([]);
	const [firstVisibleRow, setFirstVisibleRow] = useState(0);
	const [lastVisibleRow, setLastVisibleRow] = useState(9);
	const [pageSize, setPageSize] = useState(10);
	const [sortField, setSortField] = useState("profile");
	const [sortAscending, setSortAscending] = useState(true);
	const [isLoadingWasteCharacterizations, setIsLoadingWasteCharacterizations] = useState(false);	

	useEffect(() => {
		if (data) {
			setIsLoadingWasteCharacterizations(true);
			const today = new Date();
			today.setHours(0,0,0,0);

			let newData = data.filter((item) => {
				if (profile && profile != "" && (item.status != "Completed" || item.profileNumber?.toLowerCase().indexOf(profile.toLowerCase()) < 0)) {
					return false;
				}
				if (material && material != "All" && item.materialId?.toString() != material) {
					return false;
				}
				if (customer && customer != "All" && item.customerId?.toString() != customer) {
					return false;
				}
				if (status && status != "All" && item.expiresOn) {
					const expireDate = new Date(item.expiresOn);
					const expireWarningDate = new Date(expireDate);
					expireWarningDate.setDate(expireWarningDate.getDate() - expireWarningDays);					
					
					switch (status) {
						case "Pending": {
							if (item.status != "Completed") return true;
							break;
						}
						case "Active": {
							if (item.status == "Completed" && today < expireDate) return true;
							break;
						}
						case "Expired": {
							if (item.status == "Completed" && today >= expireDate) return true;
							break;
						}
						case "Expiring Soon": {
							if (item.status == "Completed" && today >= expireWarningDate && today < expireDate) return true;
							break;
						}
					}
					return false;
				}
				return true;
			}).slice();

			if (sortField) {
				switch (sortField) {
					case "profile": {
						newData.sort((a, b) => UtilityService.compareStrings(a.profileNumber, b.profileNumber));
						break;
					}
					case "material": {
						newData.sort((a, b) => UtilityService.compareStrings(a.materialName, b.materialName));
						break;
					}
					case "customer": {
						newData.sort((a, b) => UtilityService.compareStrings(a.customerName, b.customerName));
						break;
					}
					case "expires": {
						newData.sort((a, b) => UtilityService.compareNumbers(a.status == "Completed" ? a.expiresOn?.valueOf() : 0, b.status == "Completed" ? b.expiresOn?.valueOf() : 0));
						break;
					}
					default: {
						newData.sort((a, b) => UtilityService.compareStrings(a.profileNumber, b.profileNumber));
						break;
					}
				}
			}
			if (!sortAscending) newData.reverse();

			setDataView(newData);
			setFirstVisibleRow(0);
			setLastVisibleRow(pageSize - 1);
			setIsLoadingWasteCharacterizations(false);
		}
	}, [data, profile, material, customer, status, sortField, sortAscending])

	const handleHeaderClick = (field: string) => {
		if (sortField == field) {
			setSortAscending(!sortAscending);
		}
		else {
			setSortAscending(true);
			setSortField(field);
		}
	}

	const handlePageBack = () => {
		setFirstVisibleRow(firstVisibleRow - pageSize);
		setLastVisibleRow(lastVisibleRow - pageSize);
	}

	const handlePageForward = () => {
		setFirstVisibleRow(firstVisibleRow + pageSize);
		setLastVisibleRow(lastVisibleRow + pageSize);
	}

	return (
		<div className="hwi-entity-search-results-table">
			<div className="headers">
				<div className="col-profile">
					<p onClick={() => handleHeaderClick("profile")}>
						Profile #
						{" "}
						{(sortField == "profile" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "profile" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-material">
					<p onClick={() => handleHeaderClick("material")}>
						Material
						{" "}
						{(sortField == "material" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "material" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-customer">
					<p onClick={() => handleHeaderClick("customer")}>
						Customer
						{" "}
						{(sortField == "customer" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "customer" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-expires">
					<p onClick={() => handleHeaderClick("expires")}>
						Expires
						{" "}
						{(sortField == "expires" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "expires" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-controls">{""}</div>
			</div>
			<Spinner dependency={isLoadingWasteCharacterizations} text="Loading...">
				<div className="data">
					{dataView?.length > 0 ?
						(
							dataView?.map((item, index) => (
								<div className={index >= firstVisibleRow && index <= lastVisibleRow ? "row" : "row row-hidden"} key={item.id}>
									<div className={`col-profile${item.status != "Completed" ? " pending" : ""}`}><p>{item.status == "Completed" ? item.profileNumber : "Pending"}</p></div>
									<div className="col-material"><p>{item.materialName}</p></div>
									<div className="col-customer"><p>{item.customerName}</p></div>
									<div className={`col-expires${item.status != "Completed" ? " pending" : ""}`}><p>{item.status == "Completed" ? item.expiresOn?.toLocaleDateString() : "Pending"}</p></div>
									<div className="col-controls">
										<p>
											<NavLink to={"/wastecharacterization/" + item.id}>
												<Icons.ViewItem fill="#272f50" height="14px"></Icons.ViewItem>
												{" "}
												View
											</NavLink>
										</p>
									</div>
								</div>
							))
						) : (<div className="no-results">0 results</div>)}
				</div>
				{dataView?.length > 0 &&
					<div className="pager">
						<div className="pager-button"><Button variant="outlined" fullWidth={true} disabled={firstVisibleRow == 0 ? true : false} onClick={handlePageBack}>Previous</Button></div>
						<div className="pager-text">{`${firstVisibleRow + 1} to ${(lastVisibleRow + 1) <= dataView.length ? lastVisibleRow + 1 : dataView.length} of ${dataView.length}`}</div>
						<div className="pager-button"><Button variant="outlined" fullWidth={true} disabled={lastVisibleRow >= dataView.length - 1 ? true : false} onClick={handlePageForward}>Next</Button></div>
					</div>
				}
			</Spinner>
		</div>
	)
}

export default WasteCharacterizationTable;